<template>
  <div
    :class="$style.siteContainer"
    :data-test-id="$testId('siteContainer')"
  >
    <AppNotificationController />
    <AlertController :class="$style.messages" />
    <MtpNavigationBar
      v-if="showStandardView"
      ref="navBar"
      :class="['mtpHeader', $style.mtpHeader]"
      :locationLabel="locationLabel"
      :logoImg="logoImg"
    />
    <StatusBanner :class="$style.statusBanner" />
    <ViewportBanner :class="$style.banner" />
    <div
      :class="[$style.siteContent, $style.mainContainer]"
    >
      <NavPanel
        :activeLink="activeRoute"
        :class="$style.mainNav"
        :disableCommandRunner="disableCommandRunner"
        :hasBilling="hasBilling"
        :isCommandRunner="isCommandRunner"
        :isProvider="isProvider"
        :isProviderOrg="isProviderOrg"
        :ssoNavBarExperimentActive="ssoNavBarExperimentActive"
        @navPanelCollapsed="positionPasswordManagerNudge"
      />
      <RoleInfoBanner :class="$style.roleInfoBanner" />
      <Notifications />
      <MainHeader
        :class="$style.mainHeader"
        :feature="pageFeature"
        :isProviderOrg="isProviderOrg"
        :notificationsCount="notificationsCount"
        :returnPath="returnPath"
        :title="headerTitle"
        :tooltipProps="tooltipProps"
        @focusin="handleFocusIn"
        @openWhatsNew="handleOpenWhatsNew"
      />
      <WhatsNewOverlay
        v-if="whatsNewOverlayVisible"
        @close="handleCloseWhatsNewOverlay"
      />
      <SupportOverlay
        @close="restoreFocus"
      />
      <main
        :class="['mainBody', $style.mainBody]"
      >
        <router-view
          ref="mainPanel"
          name="mainPanel"
        />
        <div
          v-show="!isVueRoute"
          id="backbone"
        />
        <transition :name="$style.asideTransition">
          <router-view
            :key="$route.path"
            ref="asidePanel"
            name="asidePanel"
          />
        </transition>
        <aside
          v-show="!isVueRoute"
          id="backbone-aside-panel"
        />
      </main>
      <OnboardingSidebar />
      <InactivityModal />
      <PortalTarget name="modalPortalTarget" />
      <div
        id="messages"
        class="messages"
      />
    </div>
    <ViewportNudge />
    <PasswordManagerNudge
      :class="{
        [$style.nudgeCollapsed]: navPanelCollapsed,
        [$style.nudgeExpanded]: !navPanelCollapsed,
      }"
    />
  </div>
</template>

<script>
import 'client/AdminPortal/util/validationRules';
import * as Constants from 'client/util/Constants';
import { experiments } from 'client/GrowthEngineering/OptimizelyApi';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import AlertController from 'client/components/Alert/AlertController.vue';
import AppNotificationController from 'client/AdminPortal/Components/Notifications/AppNotificationController.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import InactivityModal from 'client/components/Modal/InactivityModal.vue';
import LocalStorageService from 'client/util/LocalStorageService';
import MspState from 'client/components/models/MspState';
import MtpNavigationBar from 'client/MTP/components/MtpNavigationBar.vue';
import Notifications from 'client/AdminPortal/Components/Notifications/Notifications.vue';
import NudgeHelper from 'client/components/Nudge/NudgeHelper';
import OnboardingSidebar from 'client/components/ImplementationChecklist/OnboardingSidebar.vue';
import OptimizelyHelper from 'client/GrowthEngineering/OptimizelyHelper';
import PasswordManagerNudge from 'client/components/Nudge/PasswordManagerNudge.vue';
import RoleInfoBanner from 'client/components/ViewportBanner/RoleInfoBanner.vue';
import StatusBanner from 'components/ViewportBanner/StatusBanner.vue';
import SupportOverlay from 'client/Support/SupportOverlay.vue';
import ViewportBanner from 'components/ViewportBanner/ViewportBanner.vue';
import ViewportNudge from 'client/components/Nudge/ViewportNudge.vue';
import WhatsNewOverlay from 'client/components/WhatsNew/WhatsNewOverlay.vue';
// Lazy loading these items allows automated testing to pass
// they don't render until everything is ready
const MainHeader = () => import('client/components/MainHeader/MainHeader.vue');
const NavPanel = () => import('client/components/NavPanel/NavPanel.vue');

const {
  DashboardConsts,
  ItemTypeConsts,
  LocalStorageConsts,
  StoreModuleMap,
} = Constants;

const { jcUndefinedLogo } = Constants.default;

const growthModule = 'GrowthModule';
const notificationsCollection = StoreModuleMap[ItemTypeConsts.notification];
const organizationsCollection = StoreModuleMap[ItemTypeConsts.organizations];
const settingsModule = 'SettingsModule';
const { upgradeAtLogin } = NudgeHelper.availableNudges;

const { navPanelCollapsed } = LocalStorageConsts;
const { ssoNavBarRenaming } = experiments;

export default {
  name: 'AdminPortalMain',

  components: {
    AlertController,
    AppNotificationController,
    InactivityModal,
    MainHeader,
    MtpNavigationBar,
    NavPanel,
    Notifications,
    OnboardingSidebar,
    PasswordManagerNudge,
    RoleInfoBanner,
    StatusBanner,
    SupportOverlay,
    ViewportBanner,
    ViewportNudge,
    WhatsNewOverlay,
  },

  EventHelper,
  LocalStorageService,
  NudgeHelper,

  props: {
    activeRoute: {
      type: String,
      required: false,
      default: '',
    },

    headerTitle: {
      type: String,
      required: false,
      default: '',
    },

    returnPath: {
      type: String,
      required: false,
      default: '',
    },

    tooltipProps: {
      type: Object,
      default() {
        return {
          linkHref: '',
          pText: '',
        };
      },
    },
  },

  data() {
    return {
      lastFocusedElement: undefined,
      navPanelCollapsed: false,
      ssoNavBarExperimentActive: false,
      whatsNewOverlayVisible: false,
    };
  },

  computed: {
    ...mapState(growthModule, [
      'optimizelyLoaded',
    ]),

    ...mapState(notificationsCollection, [
      'notificationsCount',
    ]),

    ...mapState(settingsModule, [
      'disableCommandRunner',
      'orgId',
    ]),

    ...mapGetters(settingsModule, [
      'hasBilling',
      'isCommandRunner',
      'isProvider',
      'isProviderOrg',
    ]),

    ...mapGetters(organizationsCollection, {
      organizations: 'getCollection',
    }),

    isVueRoute() {
      return this.$route.matched.length > 0;
    },

    locationLabel() {
      return get(this.organization, 'displayName', null) || DashboardConsts.locationLabel;
    },

    logoImg() {
      const now = Date.now();
      const img = get(this.organization, 'logoUrl', null) || jcUndefinedLogo;
      const logoImgUrl = `${img}#${now}`;

      return logoImgUrl;
    },

    organization() {
      return this.orgId
        ? this.organizations.find(
          organization => organization.id === this.orgId,
        )
        : {};
    },

    pageFeature() {
      let pageFeature;
      if (this.isVueRoute) {
        pageFeature = this.$route.meta.feature;
      }
      return pageFeature;
    },

    /**
     * @description Display Viewport in Standard View (Users panel etc.)
     * @param withNavBar Indicates whether or not to show the NavBar
     */
    showStandardView() {
      return MspState.isAnMsp;
    },
  },

  watch: {
    optimizelyLoaded: {
      immediate: true,
      handler(isLoaded) {
        if (isLoaded) {
          this.ssoNavBarExperimentActive = this.isNavBarSsoExperimentActive();
        }
      },
    },
  },

  created() {
    MspState.isAnMspOrChild = this.isProviderOrg;
    this.$options.NudgeHelper.setup(upgradeAtLogin);
    this.navPanelCollapsed = this.$options.LocalStorageService.getValue(navPanelCollapsed);
  },

  mounted() {
    if (MspState.isAnMsp) {
      this.removeAllOrgDetails();
    }
  },

  methods: {
    ...mapActions(organizationsCollection, {
      refreshOrgCollection: 'refreshCollection',
      removeAllOrgDetails: 'removeAllDetails',
    }),

    handleCloseWhatsNewOverlay(view) {
      this.whatsNewOverlayVisible = false;
      this.$options.EventHelper.triggerWhatsNewClosed({ view });
      this.restoreFocus();
    },

    handleFocusIn(event) {
      this.lastFocusedElement = event.target;
    },

    handleOpenWhatsNew() {
      this.whatsNewOverlayVisible = true;
      this.$options.EventHelper.triggerWhatsNewOpened();
    },

    isNavBarSsoExperimentActive() {
      return OptimizelyHelper.getExperimentVariation(ssoNavBarRenaming)
        || this.$hasFeatureFlag(this.$featureFlags.navBarSsoExperiment);
    },

    positionPasswordManagerNudge(collapsed) {
      this.navPanelCollapsed = collapsed;
    },

    restoreFocus() {
      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus();
      }
    },
  },
};
</script>

<style module>
@import './css/layout.css';

.nudgeCollapsed {
  left: navWidth;
}

.nudgeExpanded {
  left: navWidthExpanded;
}

.asideTransition,
.asideTransition-enter-active,
.asideTransition-leave-active {
  transition: transform var(--jc-transition-duration) var(--jc-transition-function);
}

.asideTransition-enter,
.asideTransition-leave-to {
  transform: translate(100%, 0);
}
</style>
